import { Experiment, FeatureRollout } from '@ms/yammer-data/dist/state/types';
import {
  useCanCurrentUserAccessAdminSettings,
  useShowStorylineAdminSettings,
} from '@ms/yammer-hooks/dist/adminSettings';
import { useIsAskMeAnythingEnabled, useIsCommunityAskMeAnythingEnabled } from '@ms/yammer-hooks/dist/askMeAnything';
import {
  useCanAccessCampaignsDashboard,
  useCanAccessCampaignsRecommendations,
  useIsCampaignSearchEnabled,
} from '@ms/yammer-hooks/dist/campaignRights';
import { useExperimentTreatmentWithoutReporting } from '@ms/yammer-hooks/dist/experiments';
import { useIsFeatureRolloutEnabled } from '@ms/yammer-hooks/dist/featureRollouts';
import { useIsBespokeCommunityCreateEditEnabled, useIsNewGroupFilesEnabled } from '@ms/yammer-hooks/dist/groups';
import {
  useCanUserAccessLeaderDirectory,
  useCanUserAccessLeadershipCorner,
} from '@ms/yammer-hooks/dist/leadershipCorner';
import { useShouldShowNewEventsLayout } from '@ms/yammer-hooks/dist/routes';
import { useCanCurrentUserAccessEnhancedUserProfile } from '@ms/yammer-hooks/dist/userProfile';
import {
  LegacyCreateGroup,
  LegacyGroupDelete,
  LegacyGroupFiles,
  LegacyGroupInfo,
  LegacyGroupInsights,
  LegacyGroupSettings,
  LegacyNetworkAdminSettings,
  LegacyUserSettings,
} from '@ms/yammer-web-components/dist/route/legacyRoutes';
import {
  articleEditPageMatchPath,
  articleNewMatchPath,
  articlePageMatchPath,
  eventPageMatchPath,
  searchPathCampaigns,
  searchPathFiles,
  searchPathGroups,
  searchPathInbox,
  searchPathQuestions,
  searchPathThreads,
  searchPathTopics,
  searchPathUsers,
} from '@ms/yammer-web-support/dist/urls';
import { useIsTeamsHub } from 'hooks/selectors';
import { useIsTeamsFullWidthLayoutEnabled } from 'hooks/useIsTeamsFullWidthLayoutEnabled';
import React, { FC } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import Page from '../Page';
import PageRoot from '../PageRoot';

import NonTeamsHubGroupFiles from './NonTeamsHubGroupFiles';
import {
  AdminSettings,
  Article,
  ArticleEdit,
  ArticleNew,
  AskMeAnything,
  AskMeAnythingCommunityEvents,
  AskMeAnythingEventCreation,
  AskMeAnythingEventEditing,
  AskMeAnythingLeadersEvents,
  AskMeAnythingUserEvents,
  Campaign,
  CampaignsDashboard,
  CampaignsHub,
  Configure,
  DelegationSettings,
  DiscoverGroups,
  Feed,
  Group,
  GroupCampaigns,
  GroupInsights,
  GroupSettings,
  Inbox,
  InsightsMain,
  LeaderDashboard,
  LeadersCuratedFeed,
  LeadersDirectory,
  LeadershipCorner,
  LegacyRouteConverter,
  LiveEvent,
  ManageAudience,
  MediaPostImmersiveView,
  NetworkThemeThreads,
  PendingPosts,
  QuestionThread,
  Questions,
  Search,
  StorylineAdminSettings,
  StorylineAdminSettingsNotification,
  StorylineHome,
  Thread,
  Topic,
  TopicNetworkQuestion,
  TopicNetworkQuestionBrowse,
  UnifiedTopic,
  UsagePolicy,
  User,
  UserAchievements,
} from './loadableComponents';

const Routes: FC = () => {
  const shouldShowNewEventsLayout = useShouldShowNewEventsLayout();
  const isFullWidthLayout = useIsTeamsFullWidthLayoutEnabled();

  const isMediaPostEnabled = useExperimentTreatmentWithoutReporting(Experiment.StorylineMediaPost) === true;
  const mediaPostImmersiveViewRoute = isMediaPostEnabled ? (
    <Route exact={true} path="/media-post/:threadId" component={MediaPostImmersiveView} />
  ) : (
    <Redirect from="/media-post/:threadId" to="/threads/:threadId" exact={true} />
  );

  return (
    <Switch>
      <Route path={eventPageMatchPath}>
        <Page
          collapseLeftSidebar={shouldShowNewEventsLayout}
          noSpacing={shouldShowNewEventsLayout}
          noScroll={shouldShowNewEventsLayout}
        >
          <LiveEvent />
        </Page>
      </Route>
      <Route exact={true} path={articleNewMatchPath}>
        <ArticleNew />
      </Route>
      <Route exact={true} path={articleEditPageMatchPath}>
        <ArticleEdit />
      </Route>
      <Route exact={true} path={articlePageMatchPath}>
        <Article />
      </Route>
      {mediaPostImmersiveViewRoute}
      <Route default={true}>
        <Page fullWidthLayout={isFullWidthLayout}>
          <ContentRoutes />
        </Page>
      </Route>
    </Switch>
  );
};

// eslint-disable-next-line complexity, max-lines-per-function
export const ContentRoutes: FC = () => {
  const canAccessAdminPage = useCanCurrentUserAccessAdminSettings();
  const canAccessCampaignPage = useCanAccessCampaignsRecommendations();
  const canUserAccessLeaderDirectory = useCanUserAccessLeaderDirectory();
  const canUserAccessLeadershipCorner = useCanUserAccessLeadershipCorner();
  const isAskMeAnythingEnabled = useIsAskMeAnythingEnabled();
  const isBespokeCommunityCreateEditEnabled = useIsBespokeCommunityCreateEditEnabled();
  const isCampaignsDashboardEnabled = useCanAccessCampaignsDashboard();
  const isCampaignSearchEnabled = useIsCampaignSearchEnabled();
  const isCommunityAskMeAnythingEnabled = useIsCommunityAskMeAnythingEnabled();
  const isEnhancedUserProfileEnabled = useCanCurrentUserAccessEnhancedUserProfile();
  const isLeaderAudienceFeedEnabled =
    useExperimentTreatmentWithoutReporting(Experiment.LeaderAudienceFeed) === 'enabled';
  const isLeadershipCuratedFeedEnabled = useExperimentTreatmentWithoutReporting(Experiment.VivaLeadershipCuratedFeed);
  const isNetworkQuestionsEnabledInGA = useExperimentTreatmentWithoutReporting(Experiment.AnswersInVivaGA) === true;
  const isNewGroupFilesEnabled = useIsNewGroupFilesEnabled();
  const isStorylineAdminEnabled = useShowStorylineAdminSettings();
  const isStorylineHomeEnabled = useIsFeatureRolloutEnabled(FeatureRollout.StorylineHome);
  const isTeamsHub = useIsTeamsHub();
  const isUnifiedTopicsEnabled = useExperimentTreatmentWithoutReporting(Experiment.UnifiedTopicsFeed) === 'enabled';
  const topicsPageComponent = isUnifiedTopicsEnabled ? UnifiedTopic : Topic;

  const userProfileStorylinePage = isEnhancedUserProfileEnabled ? (
    <Route exact={true} path="/users/:userId/storyline" component={User} />
  ) : null;

  const userProfileLeaderAudiencePage = isLeaderAudienceFeedEnabled ? (
    <Route exact={true} path="/users/:userId/audiencefeed" component={User} />
  ) : null;

  const userProfileBookmarksPage = isEnhancedUserProfileEnabled ? (
    <Route exact={true} path="/users/:userId/bookmarks" component={User} />
  ) : null;

  const storylineHomePage = isStorylineHomeEnabled ? (
    <Route exact={true} path="/storylines" component={StorylineHome} />
  ) : null;

  const legacyGroupFilesPage = !isNewGroupFilesEnabled ? (
    <Route exact={true} path="/groups/:groupId/files" component={LegacyGroupFiles} />
  ) : null;

  const nonTeamsHubGroupFilesPage =
    isNewGroupFilesEnabled && !isTeamsHub ? (
      <Route exact={true} path="/groups/:groupId/files" component={NonTeamsHubGroupFiles} />
    ) : null;

  const leadersDirectoryPages = canUserAccessLeaderDirectory
    ? [
        <Route key="Leaders" exact={true} path="/leaders" component={LeadersDirectory} />,
        <Route key="LeadersList" exact={true} path="/leaders/:subRoute" component={LeadersDirectory} />,
      ]
    : null;

  const leadershipCorner = canUserAccessLeadershipCorner ? (
    <Route exact={true} path="/leadership-corner" component={LeadershipCorner} />
  ) : null;

  const leadersCuratedFeed =
    canUserAccessLeadershipCorner && isLeadershipCuratedFeedEnabled
      ? [
          <Route
            key="LeadersCuratedFeed"
            exact={true}
            path="/leadership-corner/featured-conversations"
            component={LeadersCuratedFeed}
          />,
        ]
      : null;

  const leaderDashboard = canUserAccessLeadershipCorner ? (
    <Route exact={true} path="/leader-dashboard" component={LeaderDashboard} />
  ) : null;

  const askMeAnythingPages = isAskMeAnythingEnabled
    ? [
        <Route
          key="AskMeAnything"
          exact={true}
          path={[
            '/ama/:teamsMeetingId',
            '/ama/:teamsMeetingId/in-review',
            '/ama/:teamsMeetingId/published',
            '/ama/:teamsMeetingId/dismissed',
          ]}
          component={AskMeAnything}
        />,
        <Route
          key="AskMeAnythingUserEvents"
          exact={true}
          path="/users/:userId/events/askmeanything"
          component={AskMeAnythingUserEvents}
        />,
        <Route
          key="AskMeAnythingLeadersEvents"
          exact={true}
          path="/leadership-corner/events/askmeanything"
          component={AskMeAnythingLeadersEvents}
        />,
      ]
    : null;

  const askMeAnythingCommunityEventsPage = isCommunityAskMeAnythingEnabled ? (
    <Route path="/groups/:groupId/events/askmeanything" component={AskMeAnythingCommunityEvents} />
  ) : null;

  const storylineAdminSettingsRoutes = isStorylineAdminEnabled ? (
    <Route
      key="storylineAdminSettings"
      exact={true}
      path="/admin/settings/storyline"
      component={StorylineAdminSettings}
    />
  ) : null;

  const storylineAdminAdvancedSettingsRoutes = isStorylineAdminEnabled ? (
    <Route
      key="storylineAdminNotificationSettingsPage"
      exact={true}
      path="/admin/storyline/advanced"
      component={StorylineAdminSettingsNotification}
    />
  ) : null;

  const achievementsPage = isNetworkQuestionsEnabledInGA ? (
    <Route key="UserAchievements" exact={true} path="/achievements/:userId" component={UserAchievements} />
  ) : null;

  const adminPage = canAccessAdminPage ? <Route key="AdminHome" path="/admin" component={AdminSettings} /> : null;

  const campaignsDashboardPage = isCampaignsDashboardEnabled
    ? [
        <Route
          key="CampaignsDashboard"
          exact={true}
          path="/admin/campaigns-dashboard"
          component={CampaignsDashboard}
        />,
        <Route
          key="CampaignsDashboardSubRoute"
          exact={true}
          path="/admin/campaigns-dashboard/:campaignsDashboardSubRoute"
          component={CampaignsDashboard}
        />,
      ]
    : null;

  const searchCampaignRoute =
    isCampaignSearchEnabled && canAccessCampaignPage ? (
      <Route exact={true} path={searchPathCampaigns} component={Search} />
    ) : null;

  const searchAnswerRoute = isNetworkQuestionsEnabledInGA ? (
    <Route exact={true} path={searchPathQuestions} component={Search} />
  ) : null;

  return (
    <Switch>
      <Route exact={true} path="/ama/new/:userId?" component={AskMeAnythingEventCreation} />
      <Route exact={true} path="/ama/:teamsMeetingId/edit" component={AskMeAnythingEventEditing} />
      {askMeAnythingPages}
      <Route exact={true} path="/feed" component={Feed} />
      {achievementsPage}
      {storylineHomePage}
      <Route exact={true} path="/inbox" component={Inbox} />
      <Route exact={true} path="/inbox/:inboxFeedSubRoute" component={Inbox} />
      {campaignsDashboardPage}
      <Route key="CampaignsHub" exact={true} path="/campaigns-hub" component={CampaignsHub} />
      <Route key="GroupCampaigns" exact={true} path="/groups/:groupId/campaigns" component={GroupCampaigns} />
      <Route exact={true} path="/campaigns/:campaignId" component={Campaign} />
      <Route exact={true} path="/create-group" component={LegacyCreateGroup} />
      <Route exact={true} path="/groups/:groupId" component={Group} />
      <Route exact={true} path="/groups/:groupId/activity/:groupActivitySubRoute" component={GroupInsights} />
      <Route
        exact={true}
        path="/groups/:groupId/settings"
        component={!isBespokeCommunityCreateEditEnabled ? LegacyGroupSettings : GroupSettings}
      />
      <Route exact={true} path="/groups/:groupId/delete" component={LegacyGroupDelete} />
      {legacyGroupFilesPage}
      {nonTeamsHubGroupFilesPage}
      <Route exact={true} path="/groups/:groupId/info" component={LegacyGroupInfo} />
      <Route exact={true} path="/groups/:groupId/insights" component={LegacyGroupInsights} />
      <Route exact={true} path="/groups/:groupId/:groupFeedSubRoute" component={Group} />
      {askMeAnythingCommunityEventsPage}
      <Route exact={true} path="/threads/:threadId/:topLevelMessageId?/:secondLevelMessageId?" component={Thread} />
      <Route exact={true} path="/users/:userId" component={User} />
      {userProfileStorylinePage}
      {userProfileLeaderAudiencePage}
      {userProfileBookmarksPage}
      <Route exact={true} path="/users/:userId/pending-posts/:subRoute?" component={PendingPosts} />
      <Route exact={true} path="/topics/:topicId" component={topicsPageComponent} />
      <Route exact={true} path="/topics/:topicId/:topicFeedSubRoute" component={topicsPageComponent} />
      <Route exact={true} path="/usage-policy" component={UsagePolicy} />
      <Route exact={true} path="/user-settings" component={LegacyUserSettings} />
      <Route exact={true} path="/user-settings/delegates" component={DelegationSettings} />
      <Route exact={true} path="/network-admin" component={LegacyNetworkAdminSettings} />
      <Route exact={true} path="/configure" component={Configure} />
      <Route exact={true} path={searchPathThreads} component={Search} />
      <Route exact={true} path={searchPathGroups} component={Search} />
      <Route exact={true} path={searchPathUsers} component={Search} />
      <Route exact={true} path={searchPathFiles} component={Search} />
      <Route exact={true} path={searchPathInbox} component={Search} />
      <Route exact={true} path={searchPathTopics} component={Search} />
      {searchCampaignRoute}
      {searchAnswerRoute}
      <Route exact={true} path="/discover-groups" component={DiscoverGroups} />
      <Route exact={true} path="/discover-groups/:discoverGroupsSubRoute" component={DiscoverGroups} />
      <Route
        key="Question"
        exact={true}
        path="/questions/:threadId/:topLevelMessageId?/:secondLevelMessageId?"
        component={QuestionThread}
      />
      <Route key="Questions" exact={true} path="/questions" component={Questions} />
      <Route
        key="QuestionsTopicBrowse"
        exact={true}
        path="/topics-network-question/browse"
        component={TopicNetworkQuestionBrowse}
      />
      <Route
        key="TopicNetworkQuestionFeed"
        exact={true}
        path="/topics-network-question/:topicId"
        component={TopicNetworkQuestion}
      />
      <Route
        key="TopicNetworkQuestionFeedSubroute"
        exact={true}
        path="/topics-network-question/:topicId/:topicFeedSubRoute"
        component={TopicNetworkQuestion}
      />
      <Route
        key="ManageAudience"
        exact={true}
        path="/user-settings/leaders/manage/:userId"
        component={ManageAudience}
      />
      {leadersDirectoryPages}
      {leadershipCorner}
      {leaderDashboard}
      {leadersCuratedFeed}
      <Route exact={false} path="/insights" component={InsightsMain} />
      {storylineAdminSettingsRoutes}
      {storylineAdminAdvancedSettingsRoutes}
      {adminPage}
      <Route exact={true} path="/theme-threads/:content" component={NetworkThemeThreads} />
      <Route exact={true} path="/" component={PageRoot} />
      <Route exact={false} path="*" component={LegacyRouteConverter} />
    </Switch>
  );
};

export default Routes;
